import type {IStateStorage} from "../state";
import {debug, warn} from "../../utils/log";
import {isErrorRetryable, transformToTranslatableError} from "../../utils/error";
import {validate} from "../../pow/validate";

let currentValidationController: AbortController | null = null;
const newValidationStarted = Symbol('new validation started');
const lockLost = Symbol('lock lost');
const externalAbort = Symbol('external abort');
let currentValidation: Promise<void> | undefined;

export function abortValidation() {
    if (currentValidationController && !currentValidationController.signal.aborted) {
        debug("Aborting current validation.");
        currentValidationController.abort(externalAbort);
        currentValidationController = null;
    }
}

export type Implementation = {
    lock: (extend: boolean) => Promise<boolean>,
    unlock: () => Promise<void>
    saveAndBroadcast: (state: IStateStorage) => Promise<void>
}

export function requestValidation(state: Readonly<IStateStorage>, force = false, impl: Implementation) {
    debug("Requesting validation for solution:", state.solution, force);
    if (currentValidation && !force) {
        return currentValidation;
    }
    if (currentValidationController && !currentValidationController.signal.aborted) {
        debug("Aborting current validation due to new request.");
        currentValidationController.abort(newValidationStarted);
        currentValidationController = null;
    }
    currentValidation = doValidation(state, impl);
    return currentValidation;
}

async function doValidation(state: IStateStorage, impl: Implementation) {
    if (!await impl.lock(false)) {
        debug('Request validation aborted, already in progress');
        return;
    }
    // currentValidation is set because of the await above
    const myValidation = currentValidation!;
    let myAbortController = new AbortController();
    const abort = (reason: Symbol) => {
        debug("Validation aborted due to:", reason);
        myAbortController.abort(reason);
    }
    debug("Starting validation process for solution:", state.solution);
    if (state.state === 'error' && !isErrorRetryable(state.errorId)) {
        warn("In error state that is not retryable, aborting.");
        currentValidation = undefined;
        return impl.unlock();
    }
    try {
        // if we are in "solved" state skip propagating the "validating" state - we most likely have a valid solution.
        // prevents flickering of the UI in case the solution is valid.
        if (state.state !== 'solved') {
            await impl.saveAndBroadcast({
                state: 'validating',
                solution: '',
                rate: 0,
                errorId: '',
                errorMsg: ''
            })
        }
        if (!await impl.lock(true)) {
            // noinspection ExceptionCaughtLocallyJS
            throw lockLost;
        }
        currentValidationController = myAbortController
        const timeout = setTimeout(async () => {
            if (await impl.lock(true)) {
                if (!myAbortController.signal.aborted) {
                    debug("Validation took too long, aborting.");
                    abort(newValidationStarted);
                    if (myAbortController === currentValidationController) {
                        await impl.saveAndBroadcast({
                            state: 'error',
                            solution: '',
                            rate: 0,
                            errorId: 'tooLong',
                            errorMsg: ''
                        })
                    }
                }
            }
        }, 1000 * 60 * 30); // 30-minute timeout
        const newSolution = await validate(state.solution, myAbortController.signal, async (up) => {
            if (!await impl.lock(true)) {
                currentValidationController?.abort(lockLost);
            } else {
                if (state.state !== 'solving') {
                    await impl.saveAndBroadcast({
                        state: 'solving',
                        solution: '',
                        rate: up.rate,
                        errorId: '',
                        errorMsg: ''
                    })
                } else {
                    await impl.saveAndBroadcast({...state, rate: up.rate})
                }
            }
        })
        debug("Validation completed, new solution:", newSolution);
        currentValidationController = null;
        clearTimeout(timeout);
        if (!await impl.lock(true)) {
            // noinspection ExceptionCaughtLocallyJS
            throw lockLost;
        } else {
            await impl.saveAndBroadcast({...state, state: 'solved', solution: newSolution, errorId: '', errorMsg: ''})
        }
    } catch (err) {
        if (err !== myAbortController.signal.reason) {
            const transErr = transformToTranslatableError(err)
            await impl.saveAndBroadcast({
                state: 'error',
                solution: '',
                rate: 0,
                errorId: transErr.id,
                errorMsg: transErr.msg
            })
        }
        debug("Validation error:", err);
    } finally {
        if (currentValidationController === myAbortController) {
            currentValidationController = null;
        }
        if (currentValidation === myValidation) {
            currentValidation = undefined;
            await impl.unlock()
        }
    }
}
